import { clamp, hash2, mulberry32, valueNoise, type Rng } from './rng';
import { zoneEnabled } from './data/enabledZones';
import { HUB, linksOf, plains256, ZONES, type Side, type ZoneDef, type ZoneId } from './data/zones';
import { generatePlains256 } from './plainsMap';
export { HUB_WALL_VARIANT, PAVED_UNDER, TREE_SPECIES } from './plainsMap';
import { spotsIn, TRAINING_SPOT } from './data/trainingSpots';
import { buildAuthored, flightLevel, LAYOUT_KINDS, type BuiltLayout, type LayoutKind } from './layout';
import { LAYOUTS } from './data/layouts';
import { roadLinkWarps } from './plainsExits';
import { CHUNK, chunkIndex, type ChunkData, type ChunkStore } from './region/chunk';
import {
  finialTop,
  kindPagodaSt,
  kindRoofTrim,
  moonWallTopAt,
  pagodaLedgeAt,
  ridgeEndTop,
  roofTrimOf,
  watchtowerTopAt,
  type RoofTrim,
} from './data/roofs';

/**
 * Water is the sea, a lagoon, a pool or a stream (no walking, no jumping in); paddy (rice fields)
 * and deck (`Pier`: planks over the water, a bridge, a platform) are walked on like open ground.
 * Paved is flagstones (the look is the tile's variant, see TERRACE_LOOKS on the client); terrace is
 * the top and the stairs of a raised terrace, walked on at the height its structure gives (see
 * `surfaceAt`). Wall is a thick built wall that is itself a tile (a parapet, the arena's ring, a
 * fence, a post of the pole forest): solid to walk into, its top stood on (`level` is its top).
 */
export const Tile = {
  Floor: 0,
  Rock: 1,
  Tree: 2,
  Stele: 3,
  Building: 4,
  Water: 5,
  Paddy: 6,
  Pier: 7,
  /** The same tile as `Pier`: any walked deck of planks over water or ground. */
  Deck: 7,
  Paved: 8,
  Terrace: 9,
  Wall: 10,
} as const;
export type TileId = (typeof Tile)[keyof typeof Tile];

/** Ground that is walked on: open ground, rice paddies, pier planks, paving and terraces. */
export const isOpen = (t: number): boolean =>
  t === Tile.Floor || t === Tile.Paddy || t === Tile.Pier || t === Tile.Paved || t === Tile.Terrace;

/** Inclusive-exclusive tile rectangle: [i0, i1) × [j0, j1). */
export type Region = readonly [i0: number, i1: number, j0: number, j1: number];

/**
 * What a tile is surfaced with, to look at (`GameMap.surface`; walking never reads it): the
 * renderers' ground painters, the minimap and the heights' curbs do. The ids map to the surfaces
 * sheet (docs/art/scenery-ref, U01–U06, U17).
 */
export const Surface = {
  Grass: 0,
  /** A packed earth road or track (and the bunds between paddies). */
  Earth: 1,
  /** Stone: steps, the tops of walls. */
  Stone: 2,
  Paving: 3,
  Flagstones: 4,
  Sand: 5,
  Deck: 6,
  Gravel: 7,
  /** Moss and reeds at the water's edge. */
  Moss: 8,
  /** Vegetable beds. */
  Plots: 9,
  Paddy: 10,
  /** Snowfield (cool white; walks like gravel). */
  Snow: 11,
} as const;
export type SurfaceId = (typeof Surface)[keyof typeof Surface];

/**
 * `inn`: a two-storey inn (โรงเตี๊ยม); `boat`: a boat moored on the water (scenery only);
 * `moongate`: a garden wall one tile thick with a round moon gate (月洞門) through its middle;
 * `terrace`: a raised stone terrace with stairs down one side (see TERRACE).
 */
export type StructureKind =
  'house' | 'pagoda' | 'pavilion' | 'gate' | 'inn' | 'boat' | 'moongate' | 'terrace';

/**
 * What an authored layout's building is (docs/archive/YANYU.md §9.8): one of the kinds above, or a hall on
 * a stone base, a tower, the drum tower (a gate hall over a road), an open shed, a market stall, a
 * stable, a granary, a guard post, the hall of the six schools, a paifang, a tent, an arena, a
 * small kiosk. Each new type also has the nearest `StructureKind` (`BASE_KIND`) as its `kind`, so
 * code that knows only the kinds still draws and stands on it; `Structure.type` names it exactly.
 */
export type BuildingType =
  | StructureKind
  | 'hall'
  | 'tower'
  | 'drumtower'
  | 'shed'
  | 'stall'
  | 'stable'
  | 'granary'
  | 'guard'
  | 'recruit'
  | 'paifang'
  | 'tent'
  | 'arena'
  | 'kiosk';

/** The structure kind each building type falls back to. */
export const BASE_KIND: Readonly<Record<BuildingType, StructureKind>> = {
  house: 'house',
  pagoda: 'pagoda',
  pavilion: 'pavilion',
  gate: 'gate',
  inn: 'inn',
  boat: 'boat',
  moongate: 'moongate',
  terrace: 'terrace',
  hall: 'house',
  recruit: 'house',
  guard: 'house',
  stable: 'house',
  granary: 'house',
  tower: 'pagoda',
  drumtower: 'gate',
  paifang: 'gate',
  shed: 'pavilion',
  stall: 'pavilion',
  kiosk: 'pavilion',
  tent: 'pavilion',
  arena: 'terrace',
};

/** A side of a footprint, by compass: n is toward −y, e toward +x. */
export type Facing = 'n' | 'e' | 's' | 'w';

/**
 * A building on the map. Its footprint [i, i+w) × [j, j+h) is square for every kind except the
 * gate, whose solid tiles are only its two pillars (the road runs under the beam between them),
 * and the moon gate, a wall whose middle tiles (see `moonGateOpen`) are left open to walk through.
 */
export interface Structure {
  readonly kind: StructureKind;
  readonly i: number;
  readonly j: number;
  readonly w: number;
  readonly h: number;
  /** Gates and moon gates: the beam or wall runs along x (spanning a north–south road) or along y. */
  readonly axis?: 'x' | 'y';
  /** Look variant (roof colour and so on; a terrace's style). */
  readonly variant: number;
  /** Terraces: the side the stairs come down. */
  readonly facing?: Facing;
  /**
   * Terraces: the stairs, as [from, to) spans along the wall, counted along +x for a terrace facing
   * n or s and along +y for one facing e or w. The tiles between them are paving at ground level.
   */
  readonly stairs?: readonly (readonly [number, number])[];
  /**
   * A building that can be entered (interiors, data/interiors): its id, stable across map changes,
   * so door links can name it. Only hand-laid maps give ids for now.
   */
  readonly id?: string;
  /**
   * Its door: the footprint tile the door is in and the side it opens to. The tile beyond the door
   * (`doorFront`) is always open ground that can be walked to.
   */
  readonly door?: { readonly i: number; readonly j: number; readonly facing: Facing };
  /** An authored building's exact type (its `kind` is the nearest structure kind). */
  readonly type?: BuildingType;
  /** Its name, for the renderers and the review images (authored layouts). */
  readonly name?: string;
  /** Floor level (metres) of an authored building. */
  readonly base?: number;
  /**
   * Eaves and the roof's rise above them (metres), and the roof's shape: an authored building's
   * roof, stood on by the rules (`surfaceAt`) the way the renderers draw it.
   */
  readonly eave?: number;
  readonly rise?: number;
  readonly roof?: 'hip' | 'pyramid' | 'tiers' | 'thatch' | 'awning' | 'tent' | 'none' | 'gable';
  /**
   * Its yaw (degrees, lane KIT-EXT): the footprint [i, i+w) × [j, j+h) turned about its middle, +x
   * toward +y. Its tiles are those whose middles fall inside the turned footprint; its roof and its
   * drawing turn with it (`structureLocal`). Left out: square to the grid.
   */
  readonly rot?: number;
  /**
   * A tiered tower's eaves stood on (a pagoda's storeys, a watchtower's roof: data/roofs.ts
   * `pagodaLedgeAt`, `watchtowerTopAt`), for qinggong climbs. Left out: never stood on (`NO_ROOF`).
   */
  readonly ledges?: boolean;
  /** The district of an authored layout it belongs to. */
  readonly district?: number;
  /** A terrace's own size (pixels and tiles), in place of `TERRACE`. */
  readonly terrace?: TerraceDims;
  /**
   * An open kind's road (gates, a paifang, a moon gate's wall, the drum tower): the [from, to) span
   * of tiles along the footprint's long side the road runs through, counted from the footprint's
   * first tile (so it moves with `i`, `j`), left open; the rest of the footprint is solid (a gate's
   * posts and wings, a paifang's side bays).
   */
  readonly passage?: readonly [number, number];
}

/** A terrace: `depth` tiles of platform `height` pixels up, stairs of `steps` running `run` tiles. */
export interface TerraceDims {
  readonly height: number;
  readonly depth: number;
  readonly run: number;
  readonly steps: number;
}

/**
 * A flight of stairs or a ramp in a map's level layer: a straight incline over a rectangle from
 * `bottom` at its foot (the `down` side) to `top`, or, with `path`, along a winding path from
 * `path.from` at its first point to `path.to` at its last. Walked as a continuous slope.
 */
export interface MapFlight {
  readonly kind: 'stairs' | 'ramp';
  readonly name: string;
  readonly rect: Region;
  readonly down: Facing;
  readonly bottom: number;
  readonly top: number;
  readonly steps: number;
  readonly path?: {
    readonly pts: readonly (readonly [number, number])[];
    readonly width: number;
    readonly from: number;
    readonly to: number;
  };
}

/** Scenery of an authored layout for the renderers: a landmark tree, a lantern, a banner… */
export interface MapProp {
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  /** Height (metres) or scale, and a colour, where the kind has them. */
  readonly h?: number;
  readonly s?: number;
  readonly c?: string;
  /** Facing (degrees, 0 = south, 90 = east) of a prop that faces a way: a sign, a lantern string across a street. */
  readonly r?: number;
  /** Which look of its kind (a lantern string's span, a parasol's paper), where the kind has several. */
  readonly v?: number;
}

const FACING_STEP: Readonly<Record<Facing, readonly [number, number]>> = {
  n: [0, -1],
  e: [1, 0],
  s: [0, 1],
  w: [-1, 0],
};

/** The tile in front of a building's door (where someone coming out stands), or null without one. */
export function doorFront(st: Structure): { i: number; j: number } | null {
  if (!st.door) return null;
  const [di, dj] = FACING_STEP[st.door.facing];
  return { i: st.door.i + di, j: st.door.j + dj };
}

/** The enterable building with this id on a map, if any. */
export const buildingById = (m: GameMap, id: string): Structure | undefined =>
  m.structures.find((s) => s.id === id);

/**
 * A terrace: `depth` tiles of platform `height` pixels up, and stairs of `steps` steps running
 * `run` tiles out from it (a step is low enough to walk up; the wall is not, but can be climbed).
 */
export const TERRACE: TerraceDims = { height: 60, depth: 3, run: 4, steps: 10 };

/** A terrace's size: its own, or the shared one. */
export const terraceOf = (st: Structure): TerraceDims => st.terrace ?? TERRACE;

/** Height of a terrace's platform or stairs at a point of its footprint. */
function terraceAt(st: Structure, x: number, y: number): number {
  const f = st.facing ?? 's';
  // How far in from the foot of the stairs.
  const d = f === 's' ? st.j + st.h - y : f === 'n' ? y - st.j : f === 'e' ? st.i + st.w - x : x - st.i;
  const T = terraceOf(st);
  if (d >= T.run) return T.height;
  const step = Math.min(T.steps, Math.floor((Math.max(0, d) / T.run) * T.steps) + 1);
  return (step * T.height) / T.steps;
}

/** The structure a tile belongs to, if any. */
export const structureAt = (m: GameMap, i: number, j: number): Structure | undefined =>
  i < 0 || j < 0 || i >= m.w || j >= m.h ? undefined : m.structures[ownerAt(m, i, j) - 1];

/**
 * The tiles of a moon-gate wall `L` tiles long left open under its arch (offsets along the wall):
 * the two middle tiles of an even wall, the three of an odd one. The client cuts the arch to fit.
 */
export function moonGateOpen(L: number): number[] {
  const out: number[] = [];
  for (let u = 0; u < L; u++) if (Math.abs(u + 0.5 - L / 2) < 1.2) out.push(u);
  return out;
}

export interface Point {
  x: number;
  y: number;
}

export interface GameMap {
  readonly w: number;
  readonly h: number;
  readonly seed: number;
  readonly tiles: Uint8Array;
  /** Rock sprite variant per tile: 0–3 boulders, 4–5 border cliffs. */
  readonly variant: Uint8Array;
  readonly plaza: Point;
  readonly lair: Point;
  readonly spawn: Point;
  readonly elder: Point;
  /** Centre of the jail cell in the north-west corner. */
  readonly jail: Point;
  /** Where the world boss holds court, at the north end of the road. */
  readonly arena: Point;
  /** Warp circles at the middle of the edges, each to the neighbouring zone on that side. */
  readonly portals: readonly Warp[];
  /** The ladder's duelling ground (ลานประลอง): a walled ring south of the jail. */
  readonly duel: Point;
  readonly regions: {
    readonly wolves: Region;
    readonly bandits: Region;
    /** West of the plaza: rogue monks. East: black bears. South-east, toward the lair: the dead and assassins. */
    readonly west: Region;
    readonly east: Region;
    readonly graves: Region;
  };
  readonly structures: readonly Structure[];
  /** Ground look: the plains or the snow peak. */
  readonly biome: Biome;
  /** Which zone this is, what kind, and the colour cast over its ground. */
  readonly zone: ZoneId;
  readonly kind: ZoneDef['kind'];
  readonly tint?: readonly [number, number, number];
  /** Per tile: 1 + index of the structure standing on it, or 0. */
  readonly owner: Uint16Array;
  /** Named spots of a hand-laid map (the pier's end, the inn's door, the fields...). */
  readonly landmarks?: Readonly<Record<string, Point>>;
  /** The authored layout it was built from (layout.ts), if any: then the layers below exist. */
  readonly layout?: string;
  /**
   * Level layer: each tile's ground level in centimetres (a flight's incline at the tile's middle).
   * The rules read it through `levelAt` / `standAt` / `floorAt`; walls between levels are `edge`.
   */
  readonly level?: Int16Array;
  /** Per tile: bit 1, a wall across its east side; bit 2, across its south side (see `wallBetween`). */
  readonly edge?: Uint8Array;
  /** Flights of stairs and ramps, and per tile 1 + the index of the one it is part of (else 0). */
  readonly flights?: readonly MapFlight[];
  readonly flight?: Uint16Array;
  /** Per tile: what it is surfaced with (`Surface`), to look at. */
  readonly surface?: Uint8Array;
  /** Per tile: 1 inside the safe village (no PK there). */
  readonly safe?: Uint8Array;
  /** Per tile: 0 natural ground, 1 laid to meet the ground, 2 built with walls (for the heights' curbs). */
  readonly made?: Uint8Array;
  /** Per tile: the sea's depth class (1 shallow … 3 deep), 0 elsewhere. */
  readonly deep?: Uint8Array;
  /** Scenery of an authored layout (landmark trees, lanterns, banners…), for the renderers. */
  readonly props?: readonly MapProp[];
  /** Warps placed for zones still to come: drawn, not active. */
  readonly reserved?: readonly ReservedWarp[];
  /**
   * A floor inside a building rather than a zone's map (interiors.ts): which interior and floor,
   * and its plan of cells (data/interiors `CELL`) as laid out on this map, row by row.
   */
  readonly interior?: { readonly id: string; readonly floor: number; readonly plan: readonly string[] };
  /**
   * A chunked region (docs/archive/OPEN_WORLD.md §4.1, `region/chunks.ts` `chunkedMap`): every layer is
   * read from its chunks through the accessors, and `tiles`, `variant` and `owner` are empty.
   */
  readonly chunks?: ChunkStore;
}

export type Biome = 'plains' | 'snow';

export interface Warp extends Point {
  readonly to: ZoneId;
  /** The edge of the map it stands at (the middle of that edge, unless the layout places it). */
  readonly side: Side;
}

/** A warp kept for a zone still to come (the north ferry): where it will be, not yet active. */
export interface ReservedWarp extends Point {
  readonly name: string;
  readonly side: Side;
}

export type RegionKey = keyof GameMap['regions'];

/** Where a region's map plugs into `generateZone` (region/zone.ts registers it; lane B). */
export const zoneHooks: {
  region?: (zone: ZoneId, region: string, village: () => GameMap) => GameMap | undefined;
  /** Extra warps of a generated zone: back into a region that has a portal to it (`region/zone.ts`). */
  returns?: (zone: ZoneId) => readonly { side: Side; to: ZoneId }[];
} = {};

/**
 * A zone's map: its authored layout (the same whatever the seed), or generated from the world seed
 * shifted by the zone's own.
 */
export function generateZone(id: ZoneId, seed = 9): GameMap {
  const z = ZONES[id];
  const r = z.region && z.layout ? zoneHooks.region?.(id, z.region, () => layoutMap(id, z.layout!)) : undefined;
  if (r) return r;
  // PLAINS256 (data/zones.ts): the plains as the approved 256 m map (plainsMap.ts).
  if (id === 'plains' && plains256()) return generatePlains256(seed + z.seed);
  if (z.layout) return layoutMap(id, z.layout);
  return generateMap(seed + z.seed, z.size, z.size, z.terrain, id);
}

export const PLAZA_RADIUS = 6;
export const LAIR_RADIUS = 5.5;

export function tileAt(m: GameMap, i: number, j: number): number {
  if (i < 0 || j < 0 || i >= m.w || j >= m.h) return Tile.Rock;
  return m.chunks ? chunkOf(m.chunks, i, j).tiles[chunkIndex(i, j)]! : m.tiles[j * m.w + i]!;
}

// ── Layer accessors (docs/archive/OPEN_WORLD.md §4.1) ───────────────────────────────────────────────────
// Read layers only through these: a dense map indexes its arrays, a chunked region (lane A) will
// resolve the chunk behind the same calls. Off the map, and on a map without the layer, they give 0.

const inMap = (m: GameMap, i: number, j: number): boolean => i >= 0 && j >= 0 && i < m.w && j < m.h;
/** The chunk holding tile (i, j) (on the map), built on first touch. */
const chunkOf = (s: ChunkStore, i: number, j: number): ChunkData =>
  s.ensure((i / CHUNK) | 0, (j / CHUNK) | 0);
type LayerKey = 'variant' | 'owner' | 'edge' | 'flight' | 'surface' | 'safe' | 'made' | 'deep';
const layer = (m: GameMap, key: LayerKey, i: number, j: number): number => {
  if (!inMap(m, i, j)) return 0;
  if (m.chunks) {
    const a = chunkOf(m.chunks, i, j)[key];
    return a ? a[chunkIndex(i, j)]! : 0;
  }
  const a = m[key];
  return a ? a[j * m.w + i]! : 0;
};

export const variantAt = (m: GameMap, i: number, j: number): number => layer(m, 'variant', i, j);
export const ownerAt = (m: GameMap, i: number, j: number): number => layer(m, 'owner', i, j);
/** A tile's stored level in centimetres (not a flight's incline: see `levelAt`). */
export const levelCm = (m: GameMap, i: number, j: number): number => {
  if (!inMap(m, i, j)) return 0;
  if (m.chunks) {
    const c = chunkOf(m.chunks, i, j);
    return c.base + c.level[chunkIndex(i, j)]!;
  }
  return m.level ? m.level[j * m.w + i]! : 0;
};
export const edgeAt = (m: GameMap, i: number, j: number): number => layer(m, 'edge', i, j);
export const flightIdAt = (m: GameMap, i: number, j: number): number => layer(m, 'flight', i, j);
export const surfKind = (m: GameMap, i: number, j: number): number => layer(m, 'surface', i, j);
export const safeAt = (m: GameMap, i: number, j: number): number => layer(m, 'safe', i, j);
export const madeAt = (m: GameMap, i: number, j: number): number => layer(m, 'made', i, j);
export const deepAt = (m: GameMap, i: number, j: number): number => layer(m, 'deep', i, j);

/** Whether the map has a level layer (and so walls between levels): an authored layout or a region. */
export const hasLevels = (m: GameMap): boolean => !!m.level || !!m.chunks;

/** Writes a tile and its variant (the World's few map changes, such as a station's cleared ground). */
export function setTileAt(m: GameMap, i: number, j: number, tile: number, variant: number): void {
  if (!inMap(m, i, j)) return;
  if (m.chunks) {
    const ci = (i / CHUNK) | 0;
    const cj = (j / CHUNK) | 0;
    const c = m.chunks.ensure(ci, cj);
    m.chunks.dirty?.(ci, cj);
    c.tiles[chunkIndex(i, j)] = tile;
    c.variant[chunkIndex(i, j)] = variant;
    return;
  }
  m.tiles[j * m.w + i] = tile;
  m.variant[j * m.w + i] = variant;
}

/**
 * Heights in world pixels of the rock blocks, by variant: 0–3 boulders, 4–5 border cliffs, 6–11
 * mountainside, higher the further into a range, up to its peaks.
 */
export const ROCK_HEIGHTS = [12, 15, 10, 17, 24, 28, 40, 54, 70, 88, 108, 130] as const;

/** A boulder (not a border cliff): low enough to step up onto and walk across. */
export const isClimbable = (m: GameMap, i: number, j: number): boolean =>
  tileAt(m, i, j) === Tile.Rock && !isBorder(m, i, j) && variantAt(m, i, j) < 4;

/** Blocks walking: trees, buildings, the stele and border cliffs (boulders can be climbed). */
export const isSolid = (m: GameMap, i: number, j: number): boolean =>
  !isOpen(tileAt(m, i, j)) && !isClimbable(m, i, j);

/**
 * Roof shapes of the buildings that can be stood on (world pixels; footprint tiles). The client
 * draws its sprites from the same numbers, so the roof you land on is the roof you see.
 *
 * Lane F5 (2026-10-09): client0's eaves in metres as the c0 world draws them (art px over
 * `Z_PPM` = ART_PPM · cos 30°, so world px = art px / cos 30°): the house's walls 84 and roof 52, the
 * pavilion's columns 76 and roof 44 (was 82/52 and 86/44 world px: the drawing stood 0.66 m over
 * the rules). render3d's kit roofs follow (`KIND_ROOF`), so c0world/w3dBldg `heighten` is a no-op
 * for both. apps/client0's own 2D drawing keeps its art px.
 */
export const ROOF = {
  /** Hip roof: eaves at `eave`, ridge `rise` higher, running along x and stopping `inset` from each end. */
  house: { eave: 97, rise: 60, overhang: 0.6, inset: 1.1 },
  /** Pyramid roof. */
  pavilion: { eave: 88, rise: 51, overhang: 0.55 },
} as const;

/** Highest ledge a fighter can walk up onto without jumping (a boulder, but not a roof). */
export const STEP_UP = 18;

/** Kinds whose roofs cannot be stood on (too tall or too steep): towers, tiers, gates, tents. */
const NO_ROOF: ReadonlySet<BuildingType> = new Set<BuildingType>([
  'pagoda',
  'tower',
  'paifang',
  'tent',
  'arena',
  'moongate',
  'boat',
  'terrace',
]);

/**
 * Whether an authored structure's roof is never stood on (tiered towers, a paifang's little roofs,
 * tents, gates' passages are walked under): the surfaces checker leaves these out too.
 */
export const roofless = (st: Structure): boolean =>
  st.eave === undefined
    ? // A generated gate's posts and beam (its road is walked under).
      st.kind === 'gate'
    : !ledgesOf(st) &&
      (NO_ROOF.has(st.type ?? st.kind) || st.roof === 'tiers' || st.roof === 'tent' || st.roof === 'none');

/** Which tall building's ledges an authored structure opted into (`ledges`), if any. */
function ledgesOf(st: Structure): 'pagoda' | 'tower' | null {
  if (!st.ledges) return null;
  const t = st.type ?? st.kind;
  return t === 'pagoda' || t === 'tower' ? t : null;
}

/**
 * A map point in a structure's own frame (lane KIT-EXT): turned back by its `rot` about the
 * footprint's middle, so its roof and footprint read as square to the grid. Unchanged without `rot`.
 */
export function structureLocal(st: Structure, x: number, y: number): readonly [number, number] {
  if (!st.rot) return [x, y];
  const a = (st.rot * Math.PI) / 180;
  const [c, s] = [Math.cos(a), Math.sin(a)];
  const cx = st.i + st.w / 2;
  const cy = st.j + st.h / 2;
  const [dx, dy] = [x - cx, y - cy];
  return [cx + dx * c + dy * s, cy - dx * s + dy * c];
}

/**
 * An authored building's roof (its own `eave` and `rise`, metres): a hip roof whose ridge runs
 * along the longer side, or a pyramid; null for the kinds and roofs that cannot be stood on.
 */
function authoredRoofAt(st: Structure, x: number, y: number): number | null {
  const tall = ledgesOf(st);
  if (tall) {
    const [du, dv] = [x - st.i - st.w / 2, y - st.j - st.h / 2];
    return (tall === 'pagoda' ? pagodaLedgeAt(st, du, dv) : watchtowerTopAt(st, du, dv)) / PX_METRES;
  }
  if (roofless(st)) return null;
  return hipRoofAt(st, x, y, st.eave!, st.rise ?? 0, st.roof === 'pyramid', roofTrimOf(st));
}

/**
 * A hip roof (or a pyramid) over a footprint, eave `E` and rise `R` (metres), with its trim: its
 * ridge along the longer side; metres over the floor, in world pixels.
 */
function hipRoofAt(st: Structure, x: number, y: number, E: number, R: number, pyramid: boolean, trim: RoofTrim): number {
  const u = x - st.i;
  const v = y - st.j;
  const [along, across, L, D] = st.w >= st.h ? [u, v, st.w, st.h] : [v, u, st.h, st.w];
  let plane: number;
  if (pyramid) {
    const r = ROOF.pavilion.overhang;
    const t = Math.max(Math.abs(u - st.w / 2) / (st.w / 2 + r), Math.abs(v - st.h / 2) / (st.h / 2 + r));
    plane = E + R * Math.max(0, 1 - t);
  } else {
    const o = ROOF.house.overhang;
    const inset = trim.inset ?? ROOF.house.inset;
    const a = 1 - Math.abs(across - D / 2) / (D / 2 + o);
    const b = Math.min((along + o) / (inset + o), (L + o - along) / (inset + o));
    // A hip-and-gable (歇山): inside the gables the long slopes run on up to the ridge.
    const ug = gableAt(trim, inset, L);
    const inside = ug !== null && Math.min(along, L - along) >= ug;
    plane = E + R * Math.max(0, inside ? Math.min(1, a) : Math.min(1, a, b));
  }
  return roofTrimAt(trim, along, across, L, D, E, R, pyramid, plane) / PX_METRES;
}

/**
 * The roof's trim over a point of its plane (metres): the ridge cap, the hip caps and the
 * ornaments at the ridge's ends or the finial (data/roofs.ts), as the 3D kit draws them
 * (render3d/scenery/parts/roof.ts `hipRoof`), so feet on a ridge stand on its cap.
 */
function roofTrimAt(
  trim: RoofTrim,
  along: number,
  across: number,
  L: number,
  D: number,
  E: number,
  R: number,
  pyramid: boolean,
  plane: number,
): number {
  const o = pyramid ? ROOF.pavilion.overhang : ROOF.house.overhang;
  const Ip = pyramid ? L / 2 : Math.min(trim.inset ?? ROOF.house.inset, L / 2);
  const ug = pyramid ? null : gableAt(trim, trim.inset ?? ROOF.house.inset, L);
  // The ridge runs out to the gables on a hip-and-gable.
  const Re = ug ?? Ip;
  const top0 = E + R;
  let h = plane;
  const dv = across - D / 2;
  const [rh, rw] = trim.ridge;
  if (L - Re > Re + 1e-4) {
    const ra = Re - rw * 0.3;
    const rb = L - Re + rw * 0.3;
    if (along >= ra - 0.04 && along <= rb + 0.04 && Math.abs(dv) <= rw / 2 + 0.04) h = Math.max(h, top0 + rh);
    for (const [end, d] of [
      [ra, -1],
      [rb, 1],
    ] as const) {
      const t = ridgeEndTop(trim, along - end, dv, d);
      if (t !== null) h = Math.max(h, top0 + rh + t);
    }
  } else if (trim.ends === 'finial') {
    const t = finialTop(Math.hypot(along - L / 2, dv));
    if (t !== null) h = Math.max(h, top0 + t);
  }
  // The hips: from each end of the ridge down to the eave line's corner; on a hip-and-gable, the
  // verge cap (垂脊) down the gable's edge to the gable's foot, then the hip on to the corner.
  const [hh, hw] = trim.hip;
  const cap = (u0: number, v0: number, h0: number, u1: number, v1: number, h1: number) => {
    const [du, dw] = [u1 - u0, v1 - v0];
    const l2 = du * du + dw * dw;
    // A gable roof's hip from the verge's foot to the corner is no length (data/roofs.ts `gableOf`).
    if (l2 < 1e-9) return;
    const s = Math.max(0, Math.min(1, ((along - u0) * du + (across - v0) * dw) / l2));
    if (Math.hypot(along - u0 - s * du, across - v0 - s * dw) <= hw / 2) h = Math.max(h, h0 + (h1 - h0) * s + hh);
  };
  for (const [cu, cv] of [
    [-o, -o],
    [L + o, -o],
    [-o, D + o],
    [L + o, D + o],
  ] as const) {
    if (ug === null) {
      const ru = cu < 0 ? Ip : L - Ip;
      cap(ru, D / 2, top0, cu, cv, E);
      continue;
    }
    const gu = cu < 0 ? ug : L - ug;
    const gv = D / 2 + Math.sign(cv - D / 2) * (1 - trim.gable!) * (D / 2 + o);
    const gh = E + R * trim.gable!;
    cap(gu, D / 2, top0, gu, gv, gh);
    cap(gu, gv, gh, cu, cv, E);
  }
  return h;
}

/**
 * Where a hip-and-gable's gables stand (data/roofs.ts `gable`), in from the footprint's ends along
 * the ridge (metres), or null on a hipped roof.
 */
export function gableAt(trim: RoofTrim, inset: number, L: number): number | null {
  if (trim.gable === undefined) return null;
  const o = ROOF.house.overhang;
  return -o + trim.gable * (Math.min(inset, L / 2) + o);
}

/**
 * What a building's top is at map point (x, y), in world pixels over its floor (its roof, a tiered
 * tower's ledges), or null where it cannot be stood on: `surfaceAt` on a building's tile.
 */
export const structureTopAt = (st: Structure, x: number, y: number): number | null => roofAt(st, x, y);

/** Roof height over a point of a building's footprint, or null if it cannot be stood on. */
function roofAt(st: Structure, x: number, y: number): number | null {
  if (st.rot) [x, y] = structureLocal(st, x, y);
  if (st.eave !== undefined) return authoredRoofAt(st, x, y);
  const u = x - st.i;
  const v = y - st.j;
  const n = st.w;
  // Lane F5: the generated kinds as the 3D kit draws them (render3d/scenery), trim included, so the
  // c0 world's hub and school buildings are stood on in qinggong where they are drawn.
  if (st.kind === 'house' || st.kind === 'pavilion') {
    const r = st.kind === 'house' ? ROOF.house : ROOF.pavilion;
    return hipRoofAt(st, x, y, r.eave * PX_METRES, r.rise * PX_METRES, st.kind === 'pavilion', kindRoofTrim(st));
  }
  if (st.kind === 'pagoda')
    return pagodaLedgeAt(kindPagodaSt(st, ROOF.house.eave * PX_METRES), u - n / 2, v - st.h / 2) / PX_METRES;
  if (st.kind === 'moongate') return moonWallTopAt(st.axis === 'y' ? u - st.w / 2 : v - st.h / 2) / PX_METRES;
  return null;
}

/**
 * What can be stood on at a point, and how high it is above the ground level there (world pixels):
 * 0 on open ground, on top of a wall and on a mountainside of a map with levels, the top of a
 * boulder, the slope of a roof; null for trees, the map's border, cliffs of a map without levels and
 * the tall buildings. On a map with a level layer this is the lift over `levelAt`: the
 * rules add the level through `standAt` / `floorAt`, and the 2.5D renderer, whose world is flat,
 * draws this lift alone.
 */
export function surfaceAt(m: GameMap, x: number, y: number): number | null {
  const i = Math.floor(x);
  const j = Math.floor(y);
  const t = tileAt(m, i, j);
  if (t === Tile.Terrace) {
    const st = structureAt(m, i, j);
    return st ? terraceAt(st, x, y) : 0;
  }
  if (isOpen(t) || t === Tile.Wall) return 0;
  if (isClimbable(m, i, j)) return ROCK_HEIGHTS[variantAt(m, i, j)]!;
  // A mountainside on a map with levels: its level is the rock's top, drawn so (render3d/terrain),
  // so it is stood on, walked along where it is gentle and run up (ไต่กำแพง) where it is steep.
  // The map's own border stays a wall.
  if (t === Tile.Rock && hasLevels(m) && !isBorder(m, i, j)) return 0;
  if (t === Tile.Building) {
    const st = m.structures[ownerAt(m, i, j) - 1];
    return st ? roofAt(st, x, y) : null;
  }
  return null;
}

/** Height of whatever is underfoot at a point above the ground level (0 where there is nothing to stand on). */
export function groundAt(m: GameMap, x: number, y: number): number {
  return surfaceAt(m, x, y) ?? 0;
}

// ── Levels (docs/archive/YANYU.md §9.1) ─────────────────────────────────────────────────────────────────

/** Rule heights are world pixels: this many metres each (the 3D prototype's 1/(16√2) tile). */
export const PX_METRES = 1 / (16 * Math.SQRT2);
/** The highest step between neighbouring tiles walked without stairs or a ramp (metres). */
export const MAX_STEP = 0.35;

/**
 * The ground level at a point (metres): the incline of a flight, or the tile's level. 0 on a map
 * without a level layer, and off the map.
 */
export function levelAt(m: GameMap, x: number, y: number): number {
  if (!hasLevels(m)) return 0;
  const i = Math.floor(x);
  const j = Math.floor(y);
  if (i < 0 || j < 0 || i >= m.w || j >= m.h) return 0;
  const f = flightIdAt(m, i, j);
  if (f) return flightLevel(m.flights![f - 1]!, x, y);
  return levelCm(m, i, j) / 100;
}

/** A tile's level at its middle (metres). */
export const tileLevel = (m: GameMap, i: number, j: number): number => levelAt(m, i + 0.5, j + 0.5);

/**
 * What can be stood on at a point, in world pixels above the sea: the level plus `surfaceAt`'s
 * lift; null where there is nothing to stand on. Movement, jumps, wall-runs and landings read this.
 */
export function standAt(m: GameMap, x: number, y: number): number | null {
  const s = surfaceAt(m, x, y);
  return s === null ? null : s + levelAt(m, x, y) / PX_METRES;
}

/**
 * The water's surface at a point (metres above the sea): on a region, the chunk's water layer (the
 * sea 0, a lake its own level, a river its water), where a water tile's `level` is the bed; on an
 * authored map, and on a tile with no water, the level itself.
 */
export function waterSurfaceAt(m: GameMap, x: number, y: number): number {
  const i = Math.floor(x);
  const j = Math.floor(y);
  if (m.chunks && inMap(m, i, j)) {
    const c = chunkOf(m.chunks, i, j);
    if (c.water && c.tiles[chunkIndex(i, j)] === Tile.Water) return (c.base + c.water[chunkIndex(i, j)]!) / 100;
  }
  return levelAt(m, x, y);
}

/**
 * The height of whatever is underfoot (world pixels above the sea): the ground level where nothing
 * stands, and the water's surface over water (a skimmer stands on it; a landing there is wet).
 */
export function floorAt(m: GameMap, x: number, y: number): number {
  const s = surfaceAt(m, x, y);
  if (s === null && m.chunks && tileAt(m, Math.floor(x), Math.floor(y)) === Tile.Water)
    return waterSurfaceAt(m, x, y) / PX_METRES;
  return (s ?? 0) + levelAt(m, x, y) / PX_METRES;
}

/** Whether a wall (levels apart by more than MAX_STEP, not a flight's run) lies between two 4-neighbours. */
export function wallBetween(m: GameMap, i: number, j: number, i2: number, j2: number): boolean {
  if (!m.edge && !m.chunks) return false;
  if (j2 === j && i2 === i + 1) return !!(edgeAt(m, i, j) & 1);
  if (j2 === j && i2 === i - 1) return !!(edgeAt(m, i2, j2) & 1);
  if (i2 === i && j2 === j + 1) return !!(edgeAt(m, i, j) & 2);
  if (i2 === i && j2 === j - 1) return !!(edgeAt(m, i2, j2) & 2);
  return false;
}

/** Whether a step from tile (i, j) to its neighbour (i + di, j + dj) crosses no wall (diagonals by both ways round). */
export function stepOpen(m: GameMap, i: number, j: number, di: number, dj: number): boolean {
  if (!m.edge && !m.chunks) return true;
  if (!di || !dj) return !wallBetween(m, i, j, i + di, j + dj);
  return (
    !wallBetween(m, i, j, i + di, j) &&
    !wallBetween(m, i + di, j, i + di, j + dj) &&
    !wallBetween(m, i, j, i, j + dj) &&
    !wallBetween(m, i, j + dj, i + di, j + dj)
  );
}

/**
 * Whether a body of radius `r` moving from (x0, y0) to (x1, y1) (less than a tile) crosses a wall.
 * With `back`, a corner coming back onto the level the body stands on is no crossing: a body landed
 * with an edge over a drop (a ledge's lip) steps back from it.
 */
export function crossesWall(m: GameMap, x0: number, y0: number, x1: number, y1: number, r: number, back = false): boolean {
  if (!m.edge && !m.chunks) return false;
  const ci = Math.floor(x1);
  const cj = Math.floor(y1);
  const stays = back && ci === Math.floor(x0) && cj === Math.floor(y0);
  const own = stays ? levelCm(m, ci, cj) : 0;
  for (const [ox, oy] of [
    [-r, -r],
    [r, -r],
    [-r, r],
    [r, r],
  ] as const) {
    const i0 = Math.floor(x0 + ox);
    const j0 = Math.floor(y0 + oy);
    const di = clamp(Math.floor(x1 + ox) - i0, -1, 1);
    const dj = clamp(Math.floor(y1 + oy) - j0, -1, 1);
    if (stays && (di || dj) && Math.abs(levelCm(m, i0 + di, j0 + dj) - own) <= MAX_STEP * 100) continue;
    if ((di || dj) && !stepOpen(m, i0, j0, di, dj)) return true;
  }
  return false;
}

export const nearPlaza = (m: GameMap, x: number, y: number, r: number): boolean =>
  Math.hypot(x - m.plaza.x, y - m.plaza.y) < r;

export const nearLair = (m: GameMap, x: number, y: number, r: number): boolean =>
  Math.hypot(x - m.lair.x, y - m.lair.y) < r;

/**
 * A road underfoot: an earth or stone surface on a map with a surface layer; otherwise the dirt
 * roads that run along both axes through the plaza.
 */
export const onRoad = (m: GameMap, x: number, y: number): boolean => {
  if (m.surface || m.chunks) {
    const i = Math.floor(x);
    const j = Math.floor(y);
    if (i < 0 || j < 0 || i >= m.w || j >= m.h) return false;
    const s = surfKind(m, i, j);
    return s === Surface.Earth || s === Surface.Stone;
  }
  return Math.abs(x - m.plaza.x) < 1.1 || Math.abs(y - m.plaza.y) < 1.1;
};

/** Inside the safe village (no PK): the layout's outline where it has one, else the plaza. */
export function inSafeArea(m: GameMap, x: number, y: number): boolean {
  if (m.safe || m.chunks) return !!safeAt(m, Math.floor(x), Math.floor(y));
  return nearPlaza(m, x, y, PLAZA_RADIUS + 1);
}

export const isBorder = (m: GameMap, i: number, j: number): boolean =>
  i < 2 || j < 2 || i >= m.w - 2 || j >= m.h - 2;

/**
 * Builds the starter zone: a walled field with a central plaza and crossing roads, a gate on each
 * road out of the plaza, a village of houses to the north-west, a pagoda and pavilions, boulder
 * clusters, groves of trees, and the boss lair in the south-east. Unreachable pockets are filled
 * so every floor tile is connected to the spawn point.
 */
export function generateMap(seed = 9, w = 72, h = 72, biome: Biome = 'plains', zone: ZoneId = HUB): GameMap {
  const kind = ZONES[zone].kind;
  if (kind === 'village' && !ZONES[zone].layout)
    throw new Error(`village ${zone} has no layout (data/layouts)`);
  const links = linksOf(zone).filter((l) => zoneEnabled(l.to));
  const rnd: Rng = mulberry32(seed);
  const tiles = new Uint8Array(w * h);
  const variant = new Uint8Array(w * h);
  const owner = new Uint16Array(w * h);
  const cx = Math.floor(w / 2);
  const cy = Math.floor(h / 2);
  const structures: Structure[] = [];
  const map: GameMap = {
    w,
    h,
    seed,
    tiles,
    variant,
    plaza: { x: cx + 0.5, y: cy + 0.5 },
    lair: { x: w - 7.5, y: h - 7.5 },
    spawn: { x: cx + 0.5, y: cy + 3.6 },
    elder: { x: cx + 2.7, y: cy - 0.7 },
    jail: { x: 7, y: 7 },
    arena: { x: cx + 0.5, y: 10.5 },
    // A warp circle at the middle of each edge that has a neighbouring zone beyond it, and a warp
    // back into a region beside the circle on the region's side (their open ground joins).
    portals: [...links, ...(zoneHooks.returns?.(zone) ?? []).filter((l) => zoneEnabled(l.to)).map((l) => ({ ...l, along: 4 }))].map(
      ({ side, to, along = 0 }: { side: Side; to: ZoneId; along?: number }) => ({
        side,
        to,
        ...(side === 'n'
          ? { x: cx + 0.5 + along, y: 3.5 }
          : side === 's'
            ? { x: cx + 0.5 + along, y: h - 3.5 }
            : side === 'w'
              ? { x: 3.5, y: cy + 0.5 + along }
              : { x: w - 3.5, y: cy + 0.5 + along }),
      }),
    ),
    duel: { x: 10.5, y: 19.5 },
    regions: {
      wolves: [Math.round(w * 0.62), w - 4, 4, Math.round(h * 0.38)],
      bandits: [4, Math.round(w * 0.38), Math.round(h * 0.62), h - 4],
      west: [4, Math.round(w * 0.24), Math.round(h * 0.42), Math.round(h * 0.6)],
      east: [Math.round(w * 0.76), w - 4, Math.round(h * 0.42), Math.round(h * 0.6)],
      graves: [Math.round(w * 0.6), Math.round(w * 0.84), Math.round(h * 0.64), Math.round(h * 0.84)],
    },
    structures,
    owner,
    biome,
    zone,
    kind,
    ...(ZONES[zone].tint ? { tint: ZONES[zone].tint } : {}),
    // The town's doors and gathering places, where its people live and work (see data/folk.ts).
    ...(kind === 'hub'
      ? {
          landmarks: {
            house1: { x: cx - 10, y: cy - 3.4 },
            house5: { x: cx + 7, y: cy + 9.6 },
            house13: { x: cx - 4, y: cy + 17.6 },
            house20: { x: cx + 7, y: cy + 17.6 },
            pavilion: { x: cx - 9.5, y: cy + 11.6 },
            pagoda: { x: cx + 10, y: cy - 13.4 },
          },
        }
      : {}),
  };
  // Tiles kept free of rocks and trees: around buildings, so they can be walked round.
  const reserved = new Uint8Array(w * h);
  const keepClear = (i: number, j: number, plazaR: number, lairR: number) =>
    nearPlaza(map, i + 0.5, j + 0.5, plazaR) ||
    onRoad(map, i + 0.5, j + 0.5) ||
    nearLair(map, i + 0.5, j + 0.5, lairR) ||
    !!reserved[j * w + i];

  for (let j = 0; j < h; j++)
    for (let i = 0; i < w; i++)
      if (isBorder(map, i, j)) {
        tiles[j * w + i] = Tile.Rock;
        variant[j * w + i] = 4 + (rnd() < 0.5 ? 0 : 1);
      }

  const build = (st: Structure, solid: [number, number][]) => {
    for (let j = st.j - 1; j <= st.j + st.h; j++)
      for (let i = st.i - 1; i <= st.i + st.w; i++)
        if (i >= 0 && j >= 0 && i < w && j < h) reserved[j * w + i] = 1;
    structures.push(st);
    for (const [i, j] of solid) {
      tiles[j * w + i] = Tile.Building;
      owner[j * w + i] = structures.length;
    }
  };
  const block = (kind: StructureKind, i: number, j: number, n: number, v: number) => {
    const solid: [number, number][] = [];
    for (let dj = 0; dj < n; dj++) for (let di = 0; di < n; di++) solid.push([i + di, j + dj]);
    build({ kind, i, j, w: n, h: n, variant: v }, solid);
  };
  // The snow peak has no village: only its camp in the middle, rocks and pines.
  if (kind !== 'wild') {
    // Gates on the north and south roads out of the plaza: pillars either side of the road.
    const gd = PLAZA_RADIUS + 4;
    build({ kind: 'gate', i: cx - 2, j: cy - gd, w: 5, h: 1, axis: 'x', variant: 0 }, [
      [cx - 2, cy - gd],
      [cx + 2, cy - gd],
    ]);
    build({ kind: 'gate', i: cx - 2, j: cy + gd, w: 5, h: 1, axis: 'x', variant: 0 }, [
      [cx - 2, cy + gd],
      [cx + 2, cy + gd],
    ]);
    // A small hamlet north-west of the plaza, a pagoda to the north-east, a pavilion to the south.
    const houses: [number, number][] = [
      [-12, -8],
      [-19, -8],
      [-8, -15],
      [-15, -16],
    ];
    if (kind === 'hub') {
      // The town: a hamlet to the north-west and more houses south of the plaza.
      houses.push([5, 5], [-6, 13], [5, 13]);
      houses.forEach(([di, dj], k) => block('house', cx + di, cy + dj, 4, k % 3));
      block('pagoda', cx + 8, cy - 18, 4, 0);
      // A garden wall before the pagoda, its moon gate in line with the pagoda's door.
      const L = 10;
      const open = moonGateOpen(L);
      build(
        { kind: 'moongate', i: cx + 5, j: cy - 13, w: L, h: 1, axis: 'x', variant: 0 },
        Array.from({ length: L }, (_, u) => u)
          .filter((u) => !open.includes(u))
          .map((u): [number, number] => [cx + 5 + u, cy - 13]),
      );
      block('pavilion', cx - 11, cy + 8, 3, 0);
    } else {
      // A school's compound: its hall (a pagoda) north of the plaza, halls either side, a pavilion.
      block('pagoda', cx - 2, cy - 11, 4, 1);
      block('house', cx - 11, cy - 7, 4, 1);
      block('house', cx + 7, cy - 7, 4, 2);
      block('pavilion', cx + 6, cy + 6, 3, 0);
    }
  }
  const scale = ((w * h) / (48 * 48)) * (biome === 'snow' ? 1.4 : 1);
  for (let k = 0; k < 38 * scale; k++) {
    const ci = 2 + Math.floor(rnd() * (w - 4));
    const cj = 2 + Math.floor(rnd() * (h - 4));
    const r = 1 + rnd() * 1.8;
    for (let dj = -2; dj <= 2; dj++)
      for (let di = -2; di <= 2; di++) {
        const i = ci + di;
        const j = cj + dj;
        if (isBorder(map, i, j) || Math.hypot(di, dj) > r || rnd() >= 0.8 || keepClear(i, j, 7, 5)) continue;
        if (tiles[j * w + i] !== Tile.Floor) continue;
        tiles[j * w + i] = Tile.Rock;
        variant[j * w + i] = Math.floor(rnd() * 4);
      }
  }

  // Trees grow in groves (where the grove noise is high) with a few standing alone in between.
  for (let j = 2; j < h - 2; j++)
    for (let i = 2; i < w - 2; i++) {
      if (tiles[j * w + i] !== Tile.Floor || keepClear(i, j, 7.5, 5)) continue;
      const grove = valueNoise(i / 6 + seed * 3.1, j / 6 + 11);
      if ((grove > (biome === 'snow' ? 0.62 : 0.72) && rnd() < 0.2) || rnd() < 0.005)
        tiles[j * w + i] = Tile.Tree;
    }

  // Only the hub has the Nine Swords stele.
  if (kind === 'hub') tiles[cy * w + cx] = Tile.Stele;
  fillUnreachable(map, Math.floor(map.spawn.x), Math.floor(map.spawn.y), rnd);
  // The jail: a clear cell walled off in the corner (prisoners are kept inside by the rules).
  for (let j = 4; j < 10; j++) for (let i = 4; i < 10; i++) tiles[j * w + i] = Tile.Floor;
  // Open ground round each warp circle.
  for (const P of map.portals)
    for (let j = 2; j < h - 2; j++)
      for (let i = 2; i < w - 2; i++)
        if (Math.hypot(i + 0.5 - P.x, j + 0.5 - P.y) < (kind === 'hub' ? 1.6 : 2.6))
          tiles[j * w + i] = Tile.Floor;
  // The world boss's arena: an open ring at the north end of the road (which reaches it).
  for (let j = 3; j < 18; j++)
    for (let i = cx - 7; i <= cx + 7; i++)
      if (Math.hypot(i + 0.5 - map.arena.x, j + 0.5 - map.arena.y) < 6.5) tiles[j * w + i] = Tile.Floor;
  // The duelling ground: open inside, a ring of rock round it (fighters are brought in, not walked).
  for (let j = 11; j < 29; j++)
    for (let i = 2; i < 20; i++) {
      const d = Math.hypot(i + 0.5 - map.duel.x, j + 0.5 - map.duel.y);
      if (d < 5.5) tiles[j * w + i] = Tile.Floor;
      else if (d < 7) {
        tiles[j * w + i] = Tile.Rock;
        variant[j * w + i] = 4 + (rnd() < 0.5 ? 0 : 1);
      }
    }
  shapeTrainingSpots(map);
  return map;
}

/** Climbable boulders (see ROCK_HEIGHTS) heaped into a peak's knoll. */
const KNOLL = [3, 1, 0, 2] as const;

/**
 * Shapes the zone's training spots (data/trainingSpots.ts) into its map, last and with no dice: a
 * clearing inside each circle, and what its kind needs. A waterfall's cliff and a cave's rock rise
 * behind the circle (toward the back of the view, −x −y), the waterfall's pool at the cliff's foot;
 * a peak is a knoll of boulders to stand on; a hot spring a pool in the middle; a bamboo grove and
 * a shrine stand behind on solid ground the client draws them on. Buildings, terraces, paving, the
 * pier and the sea are left as they are.
 */
export function shapeTrainingSpots(m: GameMap): void {
  const { w, tiles, variant } = m;
  for (const s of spotsIn(m.zone)) {
    const { x, y, r } = s.area;
    const k = s.kind;
    const pool = { x: x - (r - 1) * Math.SQRT1_2, y: y - (r - 1) * Math.SQRT1_2 };
    // How far round toward the back the backdrop reaches (its facing, as a cosine) and how deep.
    const back =
      k === 'waterfall' || k === 'cave'
        ? { cos: 0.5, depth: TRAINING_SPOT.backdrop }
        : k === 'bamboo'
          ? { cos: 0.3, depth: 2 }
          : k === 'shrine'
            ? { cos: 0.9, depth: 1.2 }
            : null;
    const R = Math.ceil(r + TRAINING_SPOT.backdrop) + 1;
    for (let j = Math.floor(y) - R; j <= Math.floor(y) + R; j++)
      for (let i = Math.floor(x) - R; i <= Math.floor(x) + R; i++) {
        if (isBorder(m, i, j)) continue;
        const n = j * w + i;
        const t = tiles[n]!;
        if (t !== Tile.Floor && t !== Tile.Tree && !(t === Tile.Rock && variant[n]! < 4)) continue;
        const dx = i + 0.5 - x;
        const dy = j + 0.5 - y;
        const d = Math.hypot(dx, dy);
        const set = (tile: TileId, v = 0) => {
          tiles[n] = tile;
          variant[n] = v;
        };
        if (d < r) {
          if (k === 'peak' && d < 1.6) set(Tile.Rock, KNOLL[(i * 3 + j) & 3]);
          else if (k === 'hotSpring' && d < 1.5) set(Tile.Water);
          else if (k === 'waterfall' && Math.hypot(i + 0.5 - pool.x, j + 0.5 - pool.y) < 1.2) set(Tile.Water);
          else if (t === Tile.Tree || (t === Tile.Rock && k !== 'peak')) set(Tile.Floor);
        } else if (back && d < r + back.depth && -(dx + dy) / (d * Math.SQRT2) > back.cos) {
          if (k === 'waterfall' || k === 'cave') set(Tile.Rock, d < r + 1.2 ? 7 : 8);
          // No structure of its own: solid ground under the client's bamboo or shrine.
          else set(Tile.Building);
        }
      }
  }
}

export function fillUnreachable(m: GameMap, si: number, sj: number, rnd: Rng): void {
  const seen = new Uint8Array(m.w * m.h);
  const stack = [si + sj * m.w];
  seen[stack[0]!] = 1;
  while (stack.length) {
    const n = stack.pop()!;
    const i = n % m.w;
    const j = (n / m.w) | 0;
    for (const [di, dj] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const a = i + di;
      const b = j + dj;
      const idx = a + b * m.w;
      if (!isSolid(m, a, b) && !seen[idx]) {
        seen[idx] = 1;
        stack.push(idx);
      }
    }
  }
  for (let n = 0; n < m.w * m.h; n++)
    if (m.tiles[n] === Tile.Floor && !seen[n]) {
      m.tiles[n] = Tile.Rock;
      m.variant[n] = Math.floor(rnd() * 4);
    }
}

/** Picks a random floor tile center inside a region with some free space around it. */
export function randomFloorIn(m: GameMap, region: Region, rng: Rng): Point {
  const [i0, i1, j0, j1] = region;
  for (let n = 0; n < 400; n++) {
    const i = i0 + Math.floor(rng() * (i1 - i0));
    const j = j0 + Math.floor(rng() * (j1 - j0));
    if (tileAt(m, i, j) === Tile.Floor && !isSolid(m, i + 1, j) && !isSolid(m, i, j + 1))
      return { x: i + 0.5, y: j + 0.5 };
  }
  // A region with no bare ground (a paved square, like the demo plaza's wolf camp): its paving, before the plaza.
  for (let n = 0; n < 400; n++) {
    const i = i0 + Math.floor(rng() * (i1 - i0));
    const j = j0 + Math.floor(rng() * (j1 - j0));
    if (tileAt(m, i, j) === Tile.Paved && !isSolid(m, i, j) && !isSolid(m, i + 1, j) && !isSolid(m, i, j + 1))
      return { x: i + 0.5, y: j + 0.5 };
  }
  return { x: m.plaza.x + 3, y: m.plaza.y + 3 };
}

// ── Authored layouts (layout.ts) ────────────────────────────────────────────────────────────────

/** Each layout kind as a rule tile, its look variant and its surface. */
const KIND_TILE: Readonly<Record<LayoutKind, readonly [tile: number, variant: number, surface: SurfaceId]>> =
  {
    grass: [Tile.Floor, 0, Surface.Grass],
    sea: [Tile.Water, 0, Surface.Grass],
    sand: [Tile.Floor, 0, Surface.Sand],
    rock: [Tile.Rock, 4, Surface.Grass],
    forest: [Tile.Tree, 0, Surface.Grass],
    /** Bamboo is a tree tile of variant 1. */
    bamboo: [Tile.Tree, 1, Surface.Grass],
    road: [Tile.Floor, 0, Surface.Earth],
    pave: [Tile.Paved, 0, Surface.Paving],
    deck: [Tile.Deck, 0, Surface.Deck],
    paddy: [Tile.Paddy, 0, Surface.Paddy],
    bund: [Tile.Floor, 0, Surface.Earth],
    pond: [Tile.Water, 1, Surface.Grass],
    brook: [Tile.Water, 2, Surface.Grass],
    building: [Tile.Building, 0, Surface.Stone],
    wall: [Tile.Wall, 0, Surface.Stone],
    /** Stairs: stone steps (the flight they belong to gives their incline). */
    stairs: [Tile.Paved, 1, Surface.Stone],
    yard: [Tile.Floor, 0, Surface.Sand],
    plot: [Tile.Floor, 0, Surface.Plots],
    lotus: [Tile.Water, 3, Surface.Grass],
    /** Red rock: a cliff (variant 5). */
    redrock: [Tile.Rock, 5, Surface.Grass],
    /** A fence or palisade: a wall tile of variant 1. */
    fence: [Tile.Wall, 1, Surface.Earth],
    gravel: [Tile.Floor, 0, Surface.Gravel],
    snow: [Tile.Floor, 0, Surface.Snow],
    reeds: [Tile.Floor, 0, Surface.Moss],
    boulder: [Tile.Rock, 0, Surface.Grass],
    /** A post of the pole forest: a wall tile of variant 2. */
    post: [Tile.Wall, 2, Surface.Stone],
    /** The old banyan's trunk: a tree tile of variant 2. */
    banyan: [Tile.Tree, 2, Surface.Grass],
    /** A compound's courtyard: a paved tile in flagstones (the same rules as `pave`). */
    court: [Tile.Paved, 0, Surface.Flagstones],
  };

const built = new Map<string, BuiltLayout>();

/** A layout built once per process (it never depends on the world seed). */
export function builtLayout(id: string): BuiltLayout {
  let b = built.get(id);
  if (!b) {
    const spec = LAYOUTS[id];
    if (!spec) throw new Error(`no layout ${id}`);
    b = buildAuthored(spec);
    built.set(id, b);
  }
  return b;
}

/**
 * The wall mask of a level layer: a tile side is a wall where the ground on either side of it
 * (a flight's incline at that side's middle) differs by more than MAX_STEP. Water has no walls.
 */
export function levelEdges(
  m: Pick<GameMap, 'w' | 'h' | 'tiles' | 'level' | 'flights' | 'flight'>,
): Uint8Array {
  const { w, h } = m;
  const lv = m.level!;
  return levelEdgesIn(
    {
      w,
      h,
      tile: (i, j) => m.tiles[j * w + i]!,
      level: (i, j, x, y) => {
        const n = j * w + i;
        const f = m.flight?.[n];
        return f ? flightLevel(m.flights![f - 1]!, x, y) : lv[n]! / 100;
      },
    },
    [0, w, 0, h],
  );
}

/** What `levelEdgesIn` reads: the map's size, tiles, and the ground level of tile (i, j) at a point of it (metres). */
export interface LevelSource {
  readonly w: number;
  readonly h: number;
  tile(i: number, j: number): number;
  level(i: number, j: number, x: number, y: number): number;
}

/**
 * `levelEdges` over a window [i0, i1) × [j0, j1) of a map (a region's chunk): the wall mask of its
 * tiles, row by row over the window. A tile's east and south sides read the neighbour beyond the
 * window, so a chunk's edges need its east and south neighbours' levels.
 */
export function levelEdgesIn(src: LevelSource, region: Region): Uint8Array {
  const [i0, i1, j0, j1] = region;
  const rw = i1 - i0;
  const edge = new Uint8Array(rw * (j1 - j0));
  const apart = (i: number, j: number, a: number, b: number, x: number, y: number) =>
    Math.abs(src.level(i, j, x, y) - src.level(a, b, x, y)) > MAX_STEP + 1e-4;
  for (let j = j0; j < j1; j++)
    for (let i = i0; i < i1; i++) {
      if (src.tile(i, j) === Tile.Water) continue;
      const n = (j - j0) * rw + (i - i0);
      if (i + 1 < src.w && src.tile(i + 1, j) !== Tile.Water && apart(i, j, i + 1, j, i + 1, j + 0.5))
        edge[n]! |= 1;
      if (j + 1 < src.h && src.tile(i, j + 1) !== Tile.Water && apart(i, j, i, j + 1, i + 0.5, j + 1))
        edge[n]! |= 2;
    }
  return edge;
}

const templates = new Map<string, GameMap>();

/**
 * A zone's map from its authored layout, with fresh copies of the arrays a World may change
 * (the tiles, their variants and owners: the carriage station clears its ground).
 */
function layoutMap(zone: ZoneId, id: string): GameMap {
  const key = `${zone}:${id}`;
  let t = templates.get(key);
  if (!t) {
    t = layoutTemplate(zone, id);
    templates.set(key, t);
  }
  return { ...t, tiles: t.tiles.slice(), variant: t.variant.slice(), owner: t.owner.slice() };
}

/**
 * A zone's map from its authored layout: the layout's raster as rule tiles, its levels, walls,
 * surfaces, the safe outline, its buildings, landmarks, regions and warps. Open ground that cannot
 * be walked to from the spawn (a pocket shut in by trees or steep ground) grows trees, so nothing
 * is ever placed where no one can reach it.
 */
function layoutTemplate(zone: ZoneId, id: string): GameMap {
  const L = builtLayout(id);
  const spec = L.spec;
  const { w, h } = L;
  const N = w * h;
  const tiles = new Uint8Array(N);
  const variant = new Uint8Array(N);
  const surface = new Uint8Array(N);
  const level = new Int16Array(N);
  for (let n = 0; n < N; n++) {
    const k = LAYOUT_KINDS[L.kind[n]!]!;
    const [t, v, s] = KIND_TILE[k];
    tiles[n] = t;
    variant[n] = k === 'boulder' ? Math.floor(hash2(n % w, (n / w) | 0) * 4) : v;
    surface[n] = s;
    level[n] = Math.round(L.level[n]! * 100);
  }
  // Rock rises in the 2.5D renderer's blocks by how deep into a range it is (cliff faces at its foot).
  const depth = new Int32Array(N).fill(-1);
  let ring: number[] = [];
  for (let n = 0; n < N; n++)
    if (!(tiles[n] === Tile.Rock && variant[n]! >= 4)) {
      depth[n] = 0;
      ring.push(n);
    }
  for (let d = 1; ring.length; d++) {
    const next: number[] = [];
    for (const n of ring)
      for (const q of [n % w ? n - 1 : -1, (n + 1) % w ? n + 1 : -1, n - w, n + w])
        if (q >= 0 && q < N && depth[q] === -1) {
          depth[q] = d;
          next.push(q);
        }
    ring = next;
  }
  for (let n = 0; n < N; n++) {
    if (!(tiles[n] === Tile.Rock && variant[n]! >= 4) || LAYOUT_KINDS[L.kind[n]!] === 'redrock') continue;
    const d = depth[n]!;
    const ridge = valueNoise((n % w) / 7 + spec.seed, ((n / w) | 0) / 7 + 31);
    variant[n] =
      d <= 2
        ? 4 + (d & 1)
        : d <= 4
          ? 6
          : d <= 6
            ? 7
            : d <= 9
              ? 8
              : 8 + clamp(Math.round((ridge - 0.38) * 6), 0, 3);
  }
  const pt = (p: readonly [number, number]): Point => ({ x: p[0], y: p[1] });
  const portals: Warp[] = linksOf(zone).filter((l) => zoneEnabled(l.to)).map(({ side, to }) => {
    const at = spec.portals.find((p) => p.to === to && p.side === side)?.at;
    const edgeMiddle =
      side === 'n'
        ? { x: w / 2 + 0.5, y: 3.5 }
        : side === 's'
          ? { x: w / 2 + 0.5, y: h - 3.5 }
          : side === 'w'
            ? { x: 3.5, y: h / 2 + 0.5 }
            : { x: w - 3.5, y: h / 2 + 0.5 };
    return { side, to, ...(at ? pt(at) : edgeMiddle) };
  }).concat(roadLinkWarps(zone).filter((l) => zoneEnabled(l.to)));
  const flights = L.flights;
  const flight = L.flight.slice();
  const edge = levelEdges({ w, h, tiles, level, flights, flight });
  const map: GameMap = {
    w,
    h,
    seed: spec.seed,
    tiles,
    variant,
    plaza: pt(spec.points.plaza),
    lair: pt(spec.points.lair),
    spawn: pt(spec.points.spawn),
    elder: pt(spec.points.elder),
    jail: pt(spec.points.jail),
    arena: pt(spec.points.arena),
    portals,
    duel: pt(spec.points.duel),
    regions: spec.regions,
    structures: L.structures,
    owner: L.owner.slice(),
    biome: ZONES[zone].terrain,
    zone,
    kind: ZONES[zone].kind,
    ...(ZONES[zone].tint ? { tint: ZONES[zone].tint } : {}),
    landmarks: Object.fromEntries(Object.entries(spec.landmarks).map(([k, p]) => [k, pt(p)])),
    layout: id,
    level,
    edge,
    flights,
    flight,
    surface,
    safe: L.safe.slice(),
    made: L.made.slice(),
    deep: L.deep.slice(),
    props: L.props,
    reserved: spec.reserved.map((r) => ({ name: r.name, side: r.side, ...pt(r.at) })),
  };
  // Pockets of open natural ground no one can walk to grow trees.
  const reach = reachable(map, Math.floor(map.spawn.x), Math.floor(map.spawn.y));
  for (let n = 0; n < N; n++)
    if (
      !reach[n] &&
      tiles[n] === Tile.Floor &&
      L.made[n] !== 2 &&
      (surface[n] === Surface.Grass || surface[n] === Surface.Sand || surface[n] === Surface.Moss)
    ) {
      tiles[n] = Tile.Tree;
      variant[n] = 0;
    }
  return map;
}

/**
 * Tiles that can be walked to from (si, sj): 4-connected over open ground, never across a wall
 * between levels nor up more than a step onto a boulder or a roof.
 */
export function reachable(m: GameMap, si: number, sj: number): Uint8Array {
  return reachableIn(m, si, sj, [0, m.w, 0, m.h]);
}

/**
 * `reachable` inside a window [i0, i1) × [j0, j1) (what a region's whole-map loop becomes): the
 * mask is over the window, row by row, and the walk never leaves it.
 */
export function reachableIn(m: GameMap, si: number, sj: number, region: Region): Uint8Array {
  const [i0, i1, j0, j1] = region;
  const w = i1 - i0;
  const seen = new Uint8Array(w * (j1 - j0));
  if (si < i0 || sj < j0 || si >= i1 || sj >= j1 || isSolid(m, si, sj)) return seen;
  const stack = [(sj - j0) * w + si - i0];
  seen[stack[0]!] = 1;
  const lift = (i: number, j: number) => surfaceAt(m, i + 0.5, j + 0.5) ?? 0;
  while (stack.length) {
    const n = stack.pop()!;
    const i = (n % w) + i0;
    const j = ((n / w) | 0) + j0;
    for (const [di, dj] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const a = i + di;
      const b = j + dj;
      if (a < i0 || b < j0 || a >= i1 || b >= j1) continue;
      const q = (b - j0) * w + a - i0;
      if (seen[q] || isSolid(m, a, b) || !stepOpen(m, i, j, di, dj)) continue;
      if (Math.abs(lift(a, b) - lift(i, j)) > STEP_UP) continue;
      seen[q] = 1;
      stack.push(q);
    }
  }
  return seen;
}
